import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { TbChevronDown } from "react-icons/tb";
import logo from "../../assets/logo_nexus_sem_fundo.png";
import useScrollLock from "../../hooks/useScrollLock";
import {
  CTA_PLACEMENTS,
  DESTINATIONS,
  resolveContextFromPath,
  trackTrialCtaClick,
} from "../../analytics";
import {
  buildTrialSignupPath,
  resolveProductFromPath,
} from "../../constants/trialProduct";
import styles from "./MarketingHeader.module.css";

const SOLUTIONS = [
  {
    key: "atendimento",
    label: "Chatbot de Atendimento",
    href: "/atendimento",
    subtitle: "Atendimento 24 horas",
  },
  {
    key: "agendamento",
    label: "Chatbot de Agendamento",
    href: "/agendamento",
    subtitle: "Atendimento 24 horas e agendamento dos seus horários",
  },
];

/**
 * MarketingHeader — barra de navegação para páginas públicas/marketing.
 * Sticky, transparente no topo, branca após rolar 80px.
 * Usa IntersectionObserver (não window.addEventListener('scroll')).
 */
export default function MarketingHeader() {
  const sentinelRef = useRef(null);
  const dropdownSolutionsRef = useRef(null);
  const dropdownTimerRef = useRef(null);
  const hamburgerRef = useRef(null);
  const closeDrawerRef = useRef(null);

  const [isScrolled, setIsScrolled] = useState(false);
  const [dropdownSolutionsOpen, setDropdownSolutionsOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accordionOpen, setAccordionOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Sticky via IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsScrolled(!entry.isIntersecting),
      { threshold: 0, rootMargin: "-80px 0px 0px 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Trava a rolagem do fundo enquanto o drawer mobile está aberto.
  // A cópia local anterior escrevia `overflow = ''` direto (sem guardar o valor
  // anterior), então destravava a página por baixo de qualquer modal aberto ao
  // mesmo tempo — VideoModal e drawer convivem no MarketingLayout.
  useScrollLock(drawerOpen);

  // Foco no botão de fechar quando o drawer abre.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const raf = requestAnimationFrame(() => {
      if (closeDrawerRef.current) closeDrawerRef.current.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [drawerOpen]);

  // Fecha drawer/dropdown no Esc. UM listener só: o drawer tem precedência
  // porque é o que está por cima quando os dois estão abertos.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "Escape") return;
      if (drawerOpen) {
        closeDrawer();
      } else if (dropdownSolutionsOpen) {
        setDropdownSolutionsOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [drawerOpen, dropdownSolutionsOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const openSolutionsDropdown = useCallback(() => {
    clearTimeout(dropdownTimerRef.current);
    setDropdownSolutionsOpen(true);
  }, []);

  const scheduleCloseSolutionsDropdown = useCallback(() => {
    dropdownTimerRef.current = setTimeout(
      () => setDropdownSolutionsOpen(false),
      150,
    );
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setAccordionOpen(false);
    // Return focus to hamburger
    requestAnimationFrame(() => {
      if (hamburgerRef.current) hamburgerRef.current.focus();
    });
  }, []);

  const handleNavLinkClick = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  const handleDropdownKeyDown = useCallback((e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setDropdownSolutionsOpen((prev) => !prev);
    } else if (e.key === "Escape") {
      setDropdownSolutionsOpen(false);
    }
  }, []);

  // O header é compartilhado por todas as páginas públicas: a página, o segmento
  // e o PRODUTO vêm da rota atual, nunca de um valor fixo. Quem clica em "Testar
  // grátis" lendo `/atendimento` está pedindo o Atendimento — mandá-lo para o
  // trial de Agendamento contrariaria o que ele acabou de ler. Em rota que não
  // vende produto (`/`, `/recursos`), `product` é `undefined` e o cadastro vai
  // sem o parâmetro: o backend aplica o default.
  const handleCtaClick = useCallback(
    (placement) => {
      const product = resolveProductFromPath(location.pathname);

      trackTrialCtaClick({
        ...resolveContextFromPath(location.pathname),
        cta_placement: placement,
        destination: DESTINATIONS.LOGIN_SIGNUP,
        product,
      });

      navigate(buildTrialSignupPath(product));
    },
    [location.pathname, navigate],
  );

  return (
    <>
      {/* Sentinel para IntersectionObserver */}
      <div
        ref={sentinelRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          height: "1px",
          width: "1px",
          pointerEvents: "none",
        }}
      />

      <header
        className={[
          styles.header,
          isScrolled ? styles.scrolled : styles.transparent,
        ].join(" ")}
        role="banner"
      >
        <div className={styles.inner}>
          {/* Logo */}
          <Link
            to="/"
            className={styles.logo}
            aria-label="Nexus Chatbot — página inicial"
          >
            <img
              src={logo}
              alt=""
              width="32"
              height="32"
              className={styles.logoImg}
            />
            <span className={styles.logoText}>Nexus Chatbot</span>
          </Link>

          {/* Desktop nav */}
          <nav className={styles.desktopNav} aria-label="Navegação principal">
            {/* Dropdown Soluções */}
            <div
              className={styles.dropdownWrapper}
              ref={dropdownSolutionsRef}
              onMouseEnter={openSolutionsDropdown}
              onMouseLeave={scheduleCloseSolutionsDropdown}
            >
              <button
                className={styles.navLink}
                aria-expanded={dropdownSolutionsOpen}
                aria-haspopup="menu"
                onKeyDown={handleDropdownKeyDown}
                type="button"
              >
                Soluções{" "}
                <TbChevronDown className={styles.caret} aria-hidden="true" />
              </button>
              {dropdownSolutionsOpen && (
                <div
                  className={styles.dropdown}
                  role="menu"
                  onMouseEnter={openSolutionsDropdown}
                  onMouseLeave={scheduleCloseSolutionsDropdown}
                >
                  {SOLUTIONS.map((seg) => {
                    return (
                      <Link
                        key={seg.key}
                        to={seg.href}
                        className={styles.dropdownItem}
                        role="menuitem"
                        onClick={() => setDropdownSolutionsOpen(false)}
                      >
                        <span className={styles.segText}>
                          <span className={styles.segLabel}>{seg.label}</span>
                          <span className={styles.segSubtitle}>
                            {seg.subtitle}
                          </span>
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
            <Link to="/recursos" className={styles.navLink}>
              Recursos
            </Link>

            {/* O dropdown de Segmentos saiu do header: as 5 páginas continuam no
                ar e linkadas no rodapé, mas o menu passou a recortar por
                PRODUTO, não por ramo. */}
          </nav>

          {/* Desktop actions */}
          <div className={styles.desktopActions}>
            <Link to="/login" className={styles.loginLink}>
              Entrar
            </Link>
            <button
              type="button"
              className={styles.ctaButton}
              onClick={() => handleCtaClick(CTA_PLACEMENTS.HEADER_DESKTOP)}
            >
              Testar grátis
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            ref={hamburgerRef}
            type="button"
            className={styles.hamburger}
            aria-label="Abrir menu"
            aria-expanded={drawerOpen}
            aria-controls="mobile-drawer"
            onClick={() => setDrawerOpen(true)}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M3 6h18M3 12h18M3 18h18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </header>

      {/* Mobile Drawer */}
      {/* Overlay */}
      <div
        className={[
          styles.overlay,
          drawerOpen ? styles.overlayVisible : "",
        ].join(" ")}
        aria-hidden="true"
        onClick={closeDrawer}
      />

      <div
        id="mobile-drawer"
        className={[styles.drawer, drawerOpen ? styles.drawerOpen : ""].join(
          " ",
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Menu de navegação"
      >
        <div className={styles.drawerHeader}>
          <Link
            to="/"
            className={styles.logo}
            onClick={handleNavLinkClick}
            aria-label="Nexus Chatbot — página inicial"
          >
            <img
              src={logo}
              alt=""
              width="28"
              height="28"
              className={styles.logoImg}
            />
            <span className={styles.logoText}>Nexus Chatbot</span>
          </Link>
          <button
            ref={closeDrawerRef}
            type="button"
            className={styles.closeButton}
            aria-label="Fechar menu"
            onClick={closeDrawer}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <nav className={styles.drawerNav} aria-label="Navegação mobile">
          <Link
            to="/recursos"
            className={styles.drawerLink}
            onClick={handleNavLinkClick}
          >
            Recursos
          </Link>

          {/* Segmentos accordion */}
          <div className={styles.accordionWrapper}>
            <button
              type="button"
              className={styles.drawerLink}
              aria-expanded={accordionOpen}
              onClick={() => setAccordionOpen((prev) => !prev)}
            >
              Soluções
              <TbChevronDown
                className={[
                  styles.accordionCaret,
                  accordionOpen ? styles.caretOpen : "",
                ].join(" ")}
                aria-hidden="true"
              />
            </button>
            {accordionOpen && (
              <div className={styles.accordionPanel}>
                {SOLUTIONS.map((seg) => (
                  <Link
                    key={seg.key}
                    to={seg.href}
                    className={styles.accordionLink}
                    onClick={handleNavLinkClick}
                  >
                    {seg.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/login"
            className={styles.drawerLink}
            onClick={handleNavLinkClick}
          >
            Entrar
          </Link>
        </nav>

        <div className={styles.drawerFooter}>
          <button
            type="button"
            className={styles.drawerCta}
            onClick={() => {
              handleNavLinkClick();
              handleCtaClick(CTA_PLACEMENTS.HEADER_MOBILE);
            }}
          >
            Testar grátis
          </button>
        </div>
      </div>
    </>
  );
}
